import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import EmbeddedChat from './EmbeddedChat';
import { UserID } from '../types/user';

interface InterviewChatProps {
  uid: UserID;
  onInterviewStateChange?: (active: boolean) => void;
}

const LoadingState = () => (
  <div className="flex items-center justify-center h-full">
    <div className="text-gray-500 animate-pulse">
      Checking subscription status...
    </div>
  </div>
);

const ErrorState = ({ error }) => (
  <div className="flex flex-col items-center justify-center h-full text-center p-6">
    <h2 className="text-xl font-semibold mb-4 text-red-500">
      Subscription Error
    </h2>
    <p className="text-gray-400 mb-6">
      {error}
    </p>
    <button
      onClick={() => window.location.reload()}
      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
    >
      Retry
    </button>
  </div>
);

const SubscriptionRequired = ({ status }) => (
  <div className="flex flex-col items-center justify-center h-full text-center p-6">
    <h2 className="text-xl font-semibold mb-4">Subscription Required</h2>
    <p className="text-gray-400 mb-2">
      Current Status: {status}
    </p>
    <p className="text-gray-400 mb-6">
      You need an active subscription to access the AI Interview feature.
    </p>
    <Link
      to="/pricing"
      className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:opacity-90 transition-opacity"
    >
      View Pricing Plans
    </Link>
  </div>
);

const InterviewChat: React.FC<InterviewChatProps> = ({ uid, onInterviewStateChange }) => {
  const { 
    canStartInterview, 
    subscriptionStatus, 
    loading: subscriptionLoading, 
    error: subscriptionError 
  } = useSubscription(uid);
  const [key, setKey] = useState(Date.now());

  useEffect(() => {
    onInterviewStateChange?.(true);
    return () => {
      onInterviewStateChange?.(false);
    };
  }, [onInterviewStateChange]);

  const handleRestart = () => {
    if (window.confirm('Are you sure you want to restart? This will end your current interview.')) {
      setKey(Date.now());
    }
  };

  if (subscriptionLoading) {
    return <LoadingState />;
  }

  if (subscriptionError) {
    return <ErrorState error={subscriptionError} />;
  }

  if (!canStartInterview()) {
    return <SubscriptionRequired status={subscriptionStatus} />;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden relative">
      <button
        onClick={handleRestart}
        className="absolute top-4 right-4 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-sm font-medium transition-colors z-10 flex items-center gap-2"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Restart
      </button>
      <div className="flex-1 min-h-0">
        <EmbeddedChat key={key} uid={uid} />
      </div>
    </div>
  );
};

export default InterviewChat; 
